'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { rotateWallPhoto, getWallPhotos, HOME_WALL_PHOTOS_URL, HOME_WALL_ROTATION_MS, type WallPhoto, type WallPhotoCategory, type WallPhotosResponse } from '@/lib/homeWallPhotos';

export function useWallPhotos(direction: WallPhotoCategory, count: number) {
  const ref = useRef<HTMLElement>(null);
  const feed = useSWR<WallPhotosResponse>(count > 0 ? HOME_WALL_PHOTOS_URL : null, {
    refreshInterval: 60000,
    refreshWhenHidden: false,
    keepPreviousData: false,
  });
  const photos = useMemo(() => getWallPhotos(feed.data, direction), [feed.data, direction]);
  const key = `${direction}:${count}:${photos.map(photo => `${photo.id}:${photo.url}`).join('|')}`;
  const initial = useMemo(() => photos.slice(0, Math.max(0, count)), [count, photos]);
  const [selection, setSelection] = useState<{ key: string; photos: WallPhoto[]; nextSlot: number }>({ key, photos: initial, nextSlot: 0 });
  // При смене категории/состава папки старые фотографии не попадают даже в первый кадр.
  const current = selection.key === key ? selection.photos : initial;
  if (selection.key !== key) setSelection({ key, photos: initial, nextSlot: 0 });

  useEffect(() => {
    if (!initial.length || photos.length <= initial.length || !ref.current) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let disposed = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    const sync = () => {
      if (disposed) return;
      clearInterval(timer);
      timer = undefined;
      if (!visible || document.visibilityState !== 'visible' || motion.matches) return;
      timer = setInterval(() => {
        if (disposed) return;
        const random = Math.random();
        setSelection(previous => {
          const slot = previous.key === key ? previous.nextSlot : 0;
          return {
            key,
            photos: rotateWallPhoto(photos, previous.key === key ? previous.photos : initial, slot, () => random),
            nextSlot: (slot + 1) % initial.length,
          };
        });
      }, HOME_WALL_ROTATION_MS);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (disposed || visible === entry.isIntersecting) return;
      visible = entry.isIntersecting;
      sync();
    });
    observer.observe(ref.current);
    document.addEventListener('visibilitychange', sync);
    motion.addEventListener('change', sync);
    return () => {
      disposed = true;
      clearInterval(timer);
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
      motion.removeEventListener('change', sync);
    };
  }, [count, initial, key, photos]);

  return { ...feed, ref, photos: current };
}
